import { ComponentIds, Entity } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { Product, ProductInfoComponent, ProductType } from '@inter-ikea-storageone/range';
import { store } from 'ducks';
import { getMarketProducts } from 'ducks/products';

export const filterDoorTypes = (products: Product[]) => {
  return products.filter(p => p.coreProductId.includes('right'));
};

export const filterDoorHeights = (products: Product[], entitiesInScene: Entity[]) => {
  const framesInScene = entitiesInScene.filter(entity =>
    entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  if (framesInScene.length === 0) {
    return products;
  }

  const frameHeights = framesInScene
    .map(
      frame =>
        frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size.height
    )
    .filter((height): height is number => height !== undefined);

  const frameWithDoorHeightInScene = (product: Product) => {
    return (
      (frameHeights.includes(2020) && product.communicatedDimensions.height === 1920) ||
      (frameHeights.includes(350) && product.communicatedDimensions.height === 350)
    );
  };

  return products.filter(
    p => p.communicatedDimensions.height === 970 || frameWithDoorHeightInScene(p)
  );
};

export const filterInteriorDimensions = (products: Product[], entitiesInScene: Entity[]) => {
  const framesInScene = entitiesInScene.filter(
    e =>
      e.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
      e.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType ===
        ProductType.BILLY_FRAME_WITH_SHELVES_ASM
  );

  if (framesInScene.length <= 0) {
    return products;
  }

  const frameSizes = framesInScene.map(
    frame =>
      frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size
  );

  const frameWidths = frameSizes.map(f => f?.width);
  const frameDepths = frameSizes.map(f => f?.depth);
  const desiredDepth = frameDepths?.length === 1 ? frameDepths[0] : 280;

  const filtered = products.filter(
    p =>
      (frameWidths.includes(p.communicatedDimensions.width) &&
        p.communicatedDimensions.depth === desiredDepth) ||
      (frameWidths.includes(p.communicatedDimensions.width) && p.type === ProductType.BILLY_DESK)
  );

  return filtered;
};

export const filterInteriorColors = (products: Product[], entitiesInScene: Entity[]) => {
  const framesInScene = entitiesInScene.filter(e =>
    e.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );

  if (framesInScene.length <= 0) {
    return products;
  }

  const colorsInScene = framesInScene.map(
    frame => frame.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.design
  );

  return products.filter(
    p =>
      colorsInScene.includes(p.design) ||
      p.design === 'glass' ||
      p.type === ProductType.BILLY_DRAWER ||
      p.type === ProductType.BILLY_DESK
  );
};

export const filterExtensions = (products: Product[], entitiesInScene: Entity[]) => {
  const framesInScene = entitiesInScene.filter(e =>
    e.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
  );
  if (framesInScene.length === 0) return products;

  const frameSizesInScene = framesInScene.map(
    e =>
      e.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size
  );

  return products.filter(p =>
    frameSizesInScene.find(
      frame =>
        frame?.height === 2020 &&
        frame?.width === p.communicatedDimensions.width &&
        frame?.depth === p.communicatedDimensions.depth
    )
  );
};

export const filterDeskType = (products: Product[]) => {
  return products.filter(
    p => p.type !== ProductType.BILLY_DESK || p.coreProductId.includes('extended')
  );
};

export const hideIfCornerFittingsNotSold = (products: Product[]) => {
  const productsInMarket = getMarketProducts(store.getState());
  return productsInMarket.find(p => p.type === ProductType.BILLY_CORNER_FITTING) ? products : [];
};
